import React, { useState, useEffect } from 'react';
import firebase from 'firebase/app';
import { Form, Select, Button, message, Typography, Card, Divider, Alert, Space } from 'antd';
import { DollarOutlined } from '@ant-design/icons';

import StrikeTeamDetailsModel from '../../models/StrikeTeamDetailsModel';
import TrackerService from '../../utils/TrackerService';

import ClientPreview from './ClientPreview';
import { useTeamMembers } from './useTeamMembers';

const { Option } = Select;
const { Text, Title } = Typography;

/** The ungated manager view: one button that records the request on the
 * team doc (the onUpdateTeam trigger posts the Slack notification). */
const PayTipRequestCard = ({ requestedAt, loading, onRequestAccess }) => (
  <Card bordered={false} className="p-0" style={{ textAlign: 'left' }}>
    <Title level={4}>Pay + Tip</Title>
    <Text type="secondary">
      Charge the bill and the tip together in one payment on your Stripe Terminal.
      Pay + Tip is enabled per team: request access and we’ll set your team up.
      (Stripe Terminals only — eCOMM and PAX terminals don’t support it; web Pay + Tip is coming soon.)
    </Text>
    <Divider />
    {requestedAt ? (
      <Alert
        message="Access requested"
        description={`Your request was sent${requestedAt.toDate ? ` on ${requestedAt.toDate().toLocaleDateString()}` : ''} — we’ll be in touch.`}
        type="info"
        showIcon
      />
    ) : (
      <Button type="primary" onClick={onRequestAccess} loading={loading} icon={<DollarOutlined />}>
        Request access to Pay + Tip
      </Button>
    )}
  </Card>
);

/** The admin's pending-request banner: WHO asked (canonical ClientPreview)
 * and the grant instruction. */
const PayTipRequestedBanner = ({ requesterClientId, requestedAt }) => (
  <div style={{ marginBottom: '16px' }}>
    <Alert
      message="This team requested Pay + Tip access"
      description={(
        <Space direction="vertical" size={4}>
          <ClientPreview clientId={requesterClientId} />
          <Text type="secondary">
            {`Requested${requestedAt.toDate ? ` on ${requestedAt.toDate().toLocaleDateString()}` : ''}. Saving a split member below grants access.`}
          </Text>
        </Space>
      )}
      type="info"
      showIcon
    />
  </div>
);

const TeamPayAndTipSettings = ({ teamId, team, onLoading, changedByClientId, isAdmin }) => {
  const [form] = Form.useForm();
  const members = useTeamMembers(teamId);
  const [loading, setLoading] = useState(false);
  // ACCESS MODEL (Charles, 2026-09-29): the grant is permission to
  // CONFIGURE; the toggle is operational. Granted teams turn Pay+Tip off
  // and on and retarget the split freely — disabling NEVER demotes to
  // re-applying (revocation is a deliberate ops act: clearing
  // payTipAccessGrantedAt). Teams live before the gate are grandfathered
  // by their isChargeTotalAmount and the save below self-heals the stamp.
  const granted = !!(team?.payTipAccessGrantedAt || team?.isChargeTotalAmount);
  const requestedAt = team?.payTipRequestedAt;
  // Admin banner shows WHO asked as a person — the canonical ClientPreview
  // (avatar + displayName + admin profile link) resolves the client live.
  const requesterClientId = isAdmin ? (team?.payTipRequestedByClientId || null) : null;

  useEffect(() => {
    if (team) {
      form.setFieldsValue({
        splitClientId: team.splitClientId || null,
      });
    }
  }, [team, form]);

  const onFinish = async (values) => {
    setLoading(true);
    onLoading && onLoading({ message: 'Saving Pay + Tip settings...' });

    const { splitClientId } = values;
    const isChargeTotalAmount = !!splitClientId;

    const teamDetailsModel = new StrikeTeamDetailsModel({ teamId });
    try {
      await teamDetailsModel.updateExistingTeam({
        splitClientId: splitClientId || null,
        isChargeTotalAmount,
        // Self-heal the grant: a grandfathered team that disables must not
        // fall back to the request gate — off-then-on stays frictionless.
        ...(team?.payTipAccessGrantedAt
          ? {} : { payTipAccessGrantedAt: firebase.firestore.Timestamp.now() }),
        updatedAt: firebase.firestore.Timestamp.now(),
        updatedSplitAt: firebase.firestore.Timestamp.now(),
      });
      message.success(isChargeTotalAmount ? 'Pay + Tip settings updated' : 'Pay + Tip disabled');
    } catch (error) {
      console.error('Error updating Pay + Tip settings:', error);
      message.error('Failed to update Pay + Tip settings');
    } finally {
      setLoading(false);
      onLoading && onLoading({ message: null });
    }
  };

  const onDisable = async () => {
    form.setFieldsValue({ splitClientId: null });
    await onFinish({ splitClientId: null });
  };

  const onRequestAccess = async () => {
    setLoading(true);
    onLoading && onLoading({ message: 'Sending your request...' });
    const teamDetailsModel = new StrikeTeamDetailsModel({ teamId });
    try {
      // Flat fields on the team doc are the request of record; the
      // onUpdateTeam trigger posts the Slack notification off this write.
      await teamDetailsModel.updateExistingTeam({
        payTipRequestedAt: firebase.firestore.Timestamp.now(),
        payTipRequestedByClientId: changedByClientId || null,
        updatedAt: firebase.firestore.Timestamp.now(),
      });
      // Funnel visibility (Charles, 2026-09-30): requests show up in
      // Mixpanel as well as the admin queue — localhost counts as test,
      // the BuyTippingStands idiom.
      const isTest = window.location.hostname === 'localhost';
      new TrackerService({ isTest }).mixpanelTrack('paytip_access_requested', {
        teamId,
        teamName: team?.teamName || team?.displayName || null,
        teamOwnerClientId: team?.teamOwnerClientId || null,
        requestedByClientId: changedByClientId || null,
        currency: team?.defaultCurrency || null,
        is_test: isTest,
      });
      message.success('Request sent — we\u2019ll be in touch');
    } catch (error) {
      console.error('Error requesting Pay + Tip access:', error);
      message.error('Could not send the request');
    } finally {
      setLoading(false);
      onLoading && onLoading({ message: null });
    }
  };

  const adminMembers = (members || []).filter(m => 
    m.roles && (m.roles.includes('admin') || m.roles.includes('owner'))
  );

  // ROLE SPLIT (Charles, 2026-09-29): managers request, then change the
  // target or unenrol once granted; STRIKE ADMINS always get the form
  // (adding Pay+Tip IS the grant — the save stamps payTipAccessGrantedAt)
  // plus a banner when a team has asked, making this tab the grant surface.
  if (!granted && !isAdmin) {
    return (
      <PayTipRequestCard
        requestedAt={requestedAt}
        loading={loading}
        onRequestAccess={onRequestAccess}
      />
    );
  }

  return (
    <Card bordered={false} className="p-0" style={{ textAlign: 'left' }}>
      {isAdmin && !granted && requestedAt && (
        <PayTipRequestedBanner requesterClientId={requesterClientId} requestedAt={requestedAt} />
      )}
      <Title level={4}>Pay + Tip Settings</Title>
      <Text type="secondary">
        Pay + Tip charges the bill and the tip in one payment on the team’s Stripe Terminals.
        The payment is split to an individual client account — selecting a member below sets them
        as the split recipient and enables total-amount charging.
      </Text>
      
      <div style={{ marginTop: '16px' }}>
        <Alert
          message="Stripe Terminals only"
          description="Only Stripe terminals support Pay + Tip — eCOMM and PAX terminals do not. Web Pay + Tip is not complete yet (no zero-tip) — coming soon."
          type="warning"
          showIcon
        />
      </div>

      <Divider />

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          splitClientId: team?.splitClientId,
        }}
      >
        <Form.Item
          name="splitClientId"
          label="Select Admin or Owner for Split"
          help="The selected member will receive the split of the total amount. Setting this to none will disable Pay + Tip."
        >
          <Select
            placeholder="Select a member"
            allowClear
            loading={!members}
            style={{ width: '100%' }}
          >
            {adminMembers.map(member => (
              <Option key={member.clientId} value={member.clientId}>
                {member.displayName} ({member.roles.join(', ')})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} icon={<DollarOutlined />}>
            Save Changes
          </Button>
          {team?.splitClientId && (
            <Button 
              type="link" 
              danger 
              onClick={onDisable} 
              loading={loading}
              style={{ marginLeft: '10px' }}
            >
              Disable Pay + Tip
            </Button>
          )}
        </Form.Item>
      </Form>
    </Card>
  );
};

export default TeamPayAndTipSettings;

